import { Fade } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import {
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    RelationshipType,
    SexType,
    SupportPayer,
    SupportPayerSubscriber,
    SupportSubscriber,
    SupportSubscriberPatient,
} from '../../../api';
import { formatDate, normalizeStr, parseDate } from '../../../services';
import { Emoji } from '../../base';
import { formatPhoneNumber } from '../cards/cards';
import * as S from './insurance-card.styles';
export interface IInsuranceCardProps {
    className?: string;
    payer?: ProviderPayer | SupportPayer;
    payerSubscriber?: ProviderPayerSubscriber | SupportPayerSubscriber;
    subscriber?: ProviderSubscriber | SupportSubscriber;
    subscriberPatient?: ProviderSubscriberPatient | SupportSubscriberPatient;

    onEditPayer?: (payer: SupportPayer | ProviderPayer) => void;
}

export const InsuranceCard: React.FunctionComponent<IInsuranceCardProps> = (
    props: IInsuranceCardProps,
): React.ReactElement => {
    const { className, payer, payerSubscriber, subscriber, subscriberPatient, onEditPayer } = props;

    const renderSubscriber = (): React.ReactElement | undefined => {
        if (!subscriber) {
            return;
        }

        const dob = parseDate('Date')(subscriber.dob);
        return (
            <S.IconRow>
                {renderSexIcon(subscriber.sex)}
                <S.RowLabel>
                    <S.InsuredName>
                        {subscriber.first} {subscriber.last}
                    </S.InsuredName>
                    <S.Insured>{_.upperFirst(_.toLower(subscriber.sex))}</S.Insured>
                    <S.Insured>Born on {formatDate('MonthDayYear')(dob)}</S.Insured>
                </S.RowLabel>
            </S.IconRow>
        );
    };

    const renderSexIcon = (sex: SexType) => {
        const dict = {
            [SexType.MALE]: <Emoji type="male" />,
            [SexType.FEMALE]: <Emoji type="woman" />,
            [SexType.UNKNOWN]: <Emoji type="whiteQuestion" />,
        };

        return dict[sex];
    };

    const renderRelationshipIcon = (relationship: RelationshipType) => {
        const dict = {
            [RelationshipType.SPOUSE]: <Emoji type="coupleHeart" />,
            [RelationshipType.SELF]: <Emoji type="astronaut" />,
            [RelationshipType.CHILD]: <Emoji type="child" />,
            [RelationshipType.EMPLOYEE]: <Emoji type="briefcase" />,
            [RelationshipType.UNKNOWN]: <Emoji type="whiteQuestion" />,
            [RelationshipType.ORGANDONOR]: <Emoji type="mendingHeart" />,
            [RelationshipType.CADAVERDONOR]: <Emoji type="mendingHeart" />,
            [RelationshipType.LIFEPARTNER]: <Emoji type="peopleHoldingHands" />,
            [RelationshipType.OTHERRELATIONSHIP]: <Emoji type="peopleHoldingHands" />,
        };

        return dict[relationship];
    };

    const renderAddress = (): React.ReactElement | undefined => {
        if (subscriber && subscriber.addresses && subscriber.addresses.length) {
            const address = subscriber.addresses[0];
            return (
                <>
                    <S.Address>{address.lines.join('\n')}</S.Address>,{' '}
                    <S.CityStatePostal>
                        {address.city}, {address.state} {address.postal}
                    </S.CityStatePostal>
                    {address.country !== 'US' && <S.Country>{address.country}</S.Country>}
                </>
            );
        }
    };

    const items = onEditPayer && payer ? [{ action: () => onEditPayer(payer), label: 'Change Payer' }] : undefined;

    if (!payer) {
        return <></>;
    }

    const { name, coverage } = payer;

    return (
        <Fade appear={true} in={true}>
            <S.Wrapper className={className}>
                <S.TitleRow>
                    <S.Title>{name}</S.Title>
                    {coverage && <S.SubTitle>{normalizeStr(coverage)}</S.SubTitle>}
                </S.TitleRow>
                <S.Body>
                    {renderSubscriber()}
                    {!!subscriberPatient?.relationship && (
                        <S.IconRow>
                            {renderRelationshipIcon(subscriberPatient.relationship)}
                            <S.RowLabel>{_.upperFirst(_.lowerCase(subscriberPatient.relationship))}</S.RowLabel>
                        </S.IconRow>
                    )}
                    <S.Row>
                        <S.Col>
                            {payerSubscriber?.memberId && (
                                <S.IconRow>
                                    <Emoji type="bookmark" />
                                    <S.RowLabel>
                                        <S.LabelInfo>Member ID: </S.LabelInfo>
                                        {payerSubscriber.memberId}
                                    </S.RowLabel>
                                </S.IconRow>
                            )}
                            <S.IconRow>
                                <S.RowLabel>
                                    <S.LabelInfo>SSN: </S.LabelInfo>
                                    {subscriber?.ssn ? subscriber.ssn : <S.EmptyLabel>Empty</S.EmptyLabel>}
                                </S.RowLabel>
                            </S.IconRow>
                        </S.Col>
                    </S.Row>
                    <S.IconRow>
                        <Emoji type="houseGarden" />
                        <S.RowLabel>{renderAddress()}</S.RowLabel>
                    </S.IconRow>

                    <S.IconRow>
                        <Emoji type="phoneReceiver" />
                        <S.RowLabel>
                            {subscriber?.phone ? (
                                formatPhoneNumber(subscriber.phone)
                            ) : (
                                <S.EmptyLabel>Empty</S.EmptyLabel>
                            )}
                        </S.RowLabel>
                    </S.IconRow>
                </S.Body>
            </S.Wrapper>
        </Fade>
    );
};
